/**
 * Shared building blocks for Content Studio screens — keeps the three views
 * visually consistent without duplicating layout code.
 */
import React from 'react';

export const STYLE_OPTIONS = {
  tone: ['Authoritative', 'Conversational', 'Professional', 'Friendly', 'Bold', 'Analytical'],
  length: ['Short', 'Medium', 'Long'],
  pov: ['First person', 'Second person', 'Third person'],
  reading_level: ['General', 'Beginner', 'Expert'],
  format: ['Article', 'Listicle', 'Q&A', 'How-to', 'Op-ed'],
};

export const SUBMISSION_STATUS_STYLES = {
  uploaded: 'bg-slate-100 text-slate-700',
  generating: 'bg-amber-100 text-amber-700',
  generated: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-violet-100 text-violet-700',
  delivered: 'bg-emerald-100 text-emerald-700',
};

export const LINK_STATUS_STYLES = {
  clean: 'bg-emerald-100 text-emerald-700',
  flagged: 'bg-red-100 text-red-700',
  pending: 'bg-slate-100 text-slate-600',
};

export const StatusPill = ({ value, map, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
      map[value] || 'bg-slate-100 text-slate-600'
    }`}
  >
    {value}
  </span>
);

// `eyebrow` defaults to "Content Studio" so every existing caller is unchanged; editorial
// screens (e.g. Sources) pass their own section name rather than duplicating this component.
export const CSPageHeader = ({ title, subtitle, actions, eyebrow = 'Content Studio' }) => (
  <div className="bg-white border-b border-gray-200 px-6 py-4">
    <div className="flex items-center justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-kc-blue">
            {eyebrow}
          </span>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mt-0.5">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  </div>
);

export const CSEmptyState = ({ icon, title, message, action, testid }) => (
  <div
    data-testid={testid}
    className="flex flex-col items-center justify-center h-full text-center px-6 py-16"
  >
    <div className="w-14 h-14 rounded-2xl bg-kc-blue/10 text-kc-blue flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-500 max-w-md mb-5">{message}</p>
    {action}
  </div>
);

export const PrimaryButton = ({ children, ...props }) => (
  <button
    {...props}
    className="inline-flex items-center gap-2 bg-kc-blue text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-kc-blue-dk disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
  >
    {children}
  </button>
);

export const GhostButton = ({ children, ...props }) => (
  <button
    {...props}
    className="inline-flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
  >
    {children}
  </button>
);

// Small hover/focus "ⓘ" tooltip for junior-AM usage guidance. Uses a NAMED
// group (group/tip) so it never interferes with other `group-hover:` styles on
// the page (e.g. the article-title hover). Place next to any label, section
// header, table-column header, or checkbox.
export const InfoTip = ({ text, className = '' }) => (
  <span className={`group/tip relative inline-flex align-middle ${className}`} tabIndex={0}>
    <svg className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M18 10A8 8 0 112 10a8 8 0 0116 0zM9 9a1 1 0 112 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
    </svg>
    <span role="tooltip" className="pointer-events-none absolute left-1/2 bottom-full z-50 mb-1.5 w-64 -translate-x-1/2 rounded-lg bg-gray-900 px-2.5 py-2 text-[11px] font-normal normal-case leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/tip:opacity-100 group-focus/tip:opacity-100">
      {text}
    </span>
  </span>
);

export const Field = ({ label, children, hint, tip }) => (
  <div>
    <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
      <span>{label}</span>
      {tip && <InfoTip text={tip} />}
    </label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

export const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-kc-blue/30 focus:border-kc-blue';

// --------------------------------------------------------------------------- //
// Approval gates
//
// Three tracked sign-offs, in workflow order. Each carries a boolean (current
// state) plus a stamped-once `*_at` date recording the FIRST yes — the backend
// never clobbers it, so an un-tick/re-tick keeps the original reportable date
// and the full history lives in the article's `approvals` timeline. See
// ApprovalStage in backend/app/models/content_studio.py.
//
// `manager_approved` reads the historical `internal_review` field name; renaming
// it would break the push gate and every existing article doc.
// --------------------------------------------------------------------------- //
export const APPROVAL_STAGES = [
  {
    stage: 'sent_to_client',
    field: 'sent_to_client',
    at: 'sent_to_client_at',
    label: 'Sent to Partner',
    short: 'Sent',
    sortKey: 'sent_to_client',
    tip: 'Set automatically when the preview email is copied or opened in your mail app. Tick it by hand if you emailed the client another way.',
  },
  {
    stage: 'client_approved',
    field: 'client_approved',
    at: 'client_approved_at',
    label: 'Partner Approved',
    short: 'Partner',
    sortKey: 'client_approved',
    tip: 'Optional record that the client signed off on the shared preview. Not required to push.',
  },
  {
    stage: 'manager_approved',
    field: 'internal_review',
    at: 'internal_review_at',
    label: 'Manager Approved',
    short: 'Manager',
    sortKey: 'approved',
    tip: 'Required before an article can be distributed. Checking it asks for a compliance confirmation.',
  },
];

export const APPROVAL_STAGE_LABELS = APPROVAL_STAGES.reduce(
  (m, s) => ({ ...m, [s.stage]: s.label }), {}
);

// "Waiting on the client": the preview went out and they haven't signed off yet.
// The In progress / Pushed tab already splits the queue, so this deliberately
// says nothing about whether the article was pushed.
export const isWaitingOnClient = (a) => !!a?.sent_to_client && !a?.client_approved;

// --------------------------------------------------------------------------- //
// Article body rendering
//
// Article bodies are stored as HTML (produced by the rich-text editor). Older
// articles were stored as plain text with blank-line paragraph breaks. These
// helpers render either format identically across the editor read-view and the
// public preview page.
// --------------------------------------------------------------------------- //

// Heuristic: does this string already contain block-level HTML we generate?
const looksLikeHtml = (s) => /<(p|h[1-6]|ul|ol|li|blockquote|br|strong|em|a)\b/i.test(s || '');

const escapeHtml = (s) =>
  (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Convert legacy plain text into simple paragraph HTML (blank line = new <p>,
// single newline = <br>). Already-HTML bodies are returned untouched.
export const bodyToHtml = (body) => {
  const s = body || '';
  if (looksLikeHtml(s)) return s;
  const paras = s
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
  return paras || '<p></p>';
};

// Strip tags down to readable plain text (for clipboard copy / word counts).
export const htmlToPlainText = (body) => {
  const s = body || '';
  if (!looksLikeHtml(s)) return s;
  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = s
      .replace(/<\/(p|h[1-6]|li|blockquote|div)>/gi, '\n\n')
      .replace(/<br\s*\/?>/gi, '\n');
    return (el.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  }
  return s.replace(/<[^>]+>/g, '');
};

// Shared body styling so the editor, read-view, and public preview all match.
// Backed by real CSS in App.css (the Tailwind typography `prose` plugin is not
// installed, so we ship our own scoped rules instead).
export const PROSE_CLASS = 'cs-article-body';

export const Modal = ({ title, onClose, children, maxWidth = 'max-w-2xl', testid }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div
      data-testid={testid}
      className={`bg-white rounded-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto shadow-xl`}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button
          onClick={onClose}
          data-testid="cs-modal-close"
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

export const ConfirmDialog = ({ title, message, confirmLabel = 'Confirm', onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
    <div
      className="bg-white rounded-xl w-full max-w-md shadow-xl cursor-default"
      onClick={(e) => e.stopPropagation()}
      data-testid="cs-confirm-dialog"
    >
      <div className="px-6 py-5">
        <h3 className="text-base font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
      </div>
      <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
        <button onClick={onCancel} data-testid="cs-confirm-cancel"
          className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} data-testid="cs-confirm-ok"
          className="px-4 py-2 rounded-lg text-sm font-medium bg-kc-blue text-white hover:bg-kc-blue-dk transition-colors">
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);
